import { useLayoutEffect, useRef, useState } from 'react';
import { hhmm, rubles } from '@limuzin/kit';
import type { PassengerRideView, StopKind } from '@limuzin/passenger';
import { isNamed } from './draft';

/**
 * What the receipt in «Вы на месте»'s painting says about this ride —
 * `ReceiptLines` in the phone app.
 *
 * The painting carries the paper, the logo, «Чек поездки» over it and «Спасибо за
 * поездку!» under it. Everything between was painted out, so that the paper says
 * what this ride was rather than what a ride once was in the painting.
 *
 * No distance row: the kilometres are counted for the panel and price nothing, and
 * the passenger is not shown them (ADR 0038).
 */
export interface ReceiptLines {
  date: string;
  time: string;
  from: string;
  to: string;
  price: string;
  /**
   * The number on the pillar by the door the passenger was brought to — the house
   * of «Куда», as «5» or «128к2». Null leaves the pillar blank: a destination with
   * no number in it is not to be given one.
   */
  house: string | null;
}

/** The months as a till prints them, «8 окт. 2026». */
const SHORT_MONTHS = [
  'янв.', 'февр.', 'марта', 'апр.', 'мая', 'июня',
  'июля', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.',
];

/**
 * When the driver marked the arrival, where from and where to, and what the ride
 * cost — `receiptFor` in `active_ride.dart`. The arrival rather than the order,
 * because a receipt is made out at the end of what it is for; a ride with none
 * marked falls back to when it was ordered. A destination the passenger was to name
 * in the car has no address to print, and the row says so with a dash. The pillar
 * by the door carries the destination's house number, where its address has one.
 */
export function receiptFor(ride: PassengerRideView): ReceiptLines {
  const marked = ride.feed.find((entry) => entry.kind === 'FINISHED')?.at;
  const at = new Date(marked ?? ride.createdAt);
  const where = (kind: StopKind) =>
    ride.stops.find((stop) => stop.kind === kind && isNamed(stop))?.address ?? null;
  const destination = where('DESTINATION');
  return {
    date: `${at.getDate()} ${SHORT_MONTHS[at.getMonth()]} ${at.getFullYear()}`,
    time: hhmm(at),
    from: where('PICKUP') ?? '—',
    to: destination ?? '—',
    price: rubles(ride.payment?.totalKopeks ?? ride.fare.totalKopeks),
    house: destination === null ? null : houseNumberIn(destination),
  };
}

/** A house number standing alone, with at most a корпус, строение or литера after a space. */
const HOUSE = /^(\d+(?:[/-]\d+)?(?:[а-яёА-ЯЁa-zA-Z]{1,2}\d*)?)(?:\s+(?:к|корп|стр|с|лит)\.?\s*\S+)?$/;

/**
 * The house number in an address as the apps write one, «ул. Парковая, 5» —
 * `houseNumberIn` in `active_ride.dart`: the part after the last comma, where that
 * part is a house number and nothing else. «д. 5» and «дом 5» give «5», and «128к2»,
 * «7А» and «12/3» stay whole; «5 стр. 1» is «5». Null where there is no comma, or the
 * last part is anything else: «ул. Ленина, 12, 3 подъезд» leaves the pillar blank
 * rather than numbering the house 3.
 */
export function houseNumberIn(address: string): string | null {
  const comma = address.lastIndexOf(',');
  if (comma < 0) return null;
  const tail = address.slice(comma + 1).trim().replace(/^([дД]\.|[дД]ом)\s*/, '');
  return HOUSE.exec(tail)?.[1] ?? null;
}

/** The sheet the words are laid out on, in the full-size painting's own pixels. */
const WIDTH = 340;
const SECTION = 91;

/** What the shipped webp is cut to; the papers below map to fractions of it. */
const ASPECT = 1050 / 591;

/** The full-size painting's width, which the pillar below is measured in. */
const PAINTED = 1672;

/**
 * Where the house number stands on the pillar by the door, in the full-size
 * painting's pixels under the day sky — `PaperReceipt._pillar` in the phone app. The
 * other two skies' pillars stand a few pixels off it.
 */
const PILLAR = { left: 1544, top: 263 };
const PILLAR_SHIFT = { day: [0, 0], dark: [3, 3], night: [2, -1] } as const;

/** How narrow the painted figures were beside Manrope's own. */
const HOUSE_NARROW = 0.9;

/** The picture as `object-fit: cover` draws it in a box: its size there and where its corner falls. */
function cover(width: number, height: number) {
  const w = width > height * ASPECT ? width : height * ASPECT;
  const h = w / ASPECT;
  return { w, h, dx: (width - w) / 2, dy: (height - h) / 2 };
}

/**
 * Sheet → fractions of the picture, row by row of a 3×3 homography, fitted to the
 * three dashed rules the painting had on it. The day and night papers are the same
 * paper; the dark one stands a few pixels to the right.
 */
type Paper = readonly [number, number, number, number, number, number, number, number, number];

const DAY_PAPER: Paper = [
  0.000453187, -0.000206059, 0.570272,
  -1.03238e-05, 0.000933448, 0.522018,
  -0.000175498, -0.000165909, 1,
];
const DARK_PAPER: Paper = [
  0.000452024, -0.000207297, 0.57387,
  -8.27543e-06, 0.000928661, 0.52336,
  -0.000171815, -0.000166986, 1,
];

/**
 * The cover crop of a box [width] by [height], then the paper, as a CSS
 * `matrix3d` — the same arithmetic as `PaperReceipt._onto` in the phone app.
 */
function onto(width: number, height: number, paper: Paper): string {
  const { w, h, dx, dy } = cover(width, height);
  const [p0, p1, p2, p3, p4, p5, p6, p7, p8] = paper;
  // Column by column, as `matrix3d` takes them.
  return `matrix3d(${[
    w * p0 + dx * p6, h * p3 + dy * p6, 0, p6,
    w * p1 + dx * p7, h * p4 + dy * p7, 0, p7,
    0, 0, 1, 0,
    w * p2 + dx * p8, h * p5 + dy * p8, 0, p8,
  ].join(',')})`;
}

/**
 * The words of [lines], written onto the paper of `zavershen` as the picture stands
 * in the box this fills — `PaperReceipt` in the phone app. They are laid out flat
 * and bent onto the paper by one perspective matrix per sky, recomputed whenever the
 * box changes size, because the crop that decides where the paper is does.
 *
 * The ink is taken off the painting under each sky: the night paper is lit warm by
 * the porch, and a navy that is right on white reads as pasted on there.
 *
 * The house number goes on the pillar by the door, which faces the viewer square on
 * and needs no more than scaling down with the picture. A number too wide for it is
 * set smaller rather than cut: a house number missing a figure is somebody else's.
 */
export function PaperReceipt({ lines, night, dark }: {
  lines: ReceiptLines;
  night: boolean;
  dark: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return;
    const measure = () =>
      setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const sky = night ? 'night' : dark ? 'dark' : 'day';
  const paper = !night && dark ? DARK_PAPER : DAY_PAPER;
  const pillar = (width: number, height: number) => {
    const { w, dx, dy } = cover(width, height);
    const scale = w / PAINTED;
    const [x, y] = PILLAR_SHIFT[sky];
    return `translate(${dx + (PILLAR.left + x) * scale}px, ${dy + (PILLAR.top + y) * scale}px) scale(${scale * HOUSE_NARROW}, ${scale})`;
  };
  return (
    // The paper says what the panel under it says, so it is not read out again.
    <div ref={box} className={`paper-receipt ${sky}`} aria-hidden="true">
      {size && (
        <div
          className="paper-sheet"
          style={{ width: WIDTH, height: SECTION * 3, transform: onto(size.width, size.height, paper) }}
        >
          <div className="paper-section">
            <ReceiptRow label="Дата" value={lines.date} />
            <ReceiptRow label="Время" value={lines.time} />
          </div>
          <div className="paper-section">
            <ReceiptRow label="Откуда" value={lines.from} />
            <ReceiptRow label="Куда" value={lines.to} />
          </div>
          <div className="paper-section">
            <ReceiptRow label="Стоимость" value={lines.price} total />
          </div>
        </div>
      )}
      {size && lines.house !== null && (
        <div className="paper-house" style={{ transform: pillar(size.width, size.height) }}>
          <FitValue text={lines.house} least={0} className="paper-house-number" />
        </div>
      )}
    </div>
  );
}

function ReceiptRow({ label, value, total = false }: {
  label: string;
  value: string;
  total?: boolean;
}) {
  return (
    <div className={total ? 'paper-row total' : 'paper-row'}>
      <span>{label}</span>
      <FitValue text={value} />
    </div>
  );
}

/** The least a value on the paper is set at before it is cut — `_Value._least` in the phone app. */
const LEAST = 0.7;

/**
 * A value on the paper, kept to one line. One too long for its row is set smaller
 * first — down to {@link LEAST} of its size — and cut only past that: «ул. Маршала
 * Жукова, 1…» has lost the one part of the address that says which house. Measured
 * on the flat sheet, which the matrix bends only after layout, and again once
 * Manrope has come in, because a fallback face measures differently.
 */
function FitValue({ text, least = LEAST, className = 'paper-value' }: {
  text: string;
  /** Nought for the house number, which is never cut. */
  least?: number;
  className?: string;
}) {
  const span = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const element = span.current;
    if (!element) return;
    let live = true;
    // Written on the element rather than kept in state: it is a measurement of
    // what React has already laid out, and nothing else reads it.
    const fit = () => {
      if (!live) return;
      element.style.fontSize = '';
      const need = element.scrollWidth;
      const room = element.clientWidth;
      if (need <= room) return;
      // A pixel short of the room: both widths come rounded to whole pixels, and
      // a value set to fill it exactly can overflow it by a fraction and be cut.
      const size = parseFloat(getComputedStyle(element).fontSize);
      element.style.fontSize = `${size * Math.max(least, (room - 1) / need)}px`;
    };
    fit();
    // `fonts.ready` alone settles before a face nobody has asked for yet starts
    // loading — Firefox measured the fallback and never looked again.
    const face = getComputedStyle(element);
    void document.fonts
      ?.load(`${face.fontWeight} ${face.fontSize} ${face.fontFamily}`)
      .then(fit, () => {});
    return () => {
      live = false;
    };
  }, [text, least]);
  return (
    <span ref={span} className={className}>
      {text}
    </span>
  );
}
